/**
 * The second of two gates, and the one that answers "does *Otto* know you".
 *
 * `AuthGate` answers a different question — "has Entra signed you in" — and it
 * is the only thing standing between a browser and the console today. That is
 * not enough: an Entra account with no `people` row is authenticated but has no
 * tenant, and the API refuses every tenant-scoped route for it. Without this
 * gate the console rendered the full client shell anyway, because
 * `ConsoleSessionProvider` falls back to `role: 'viewer'` when `GET /me` fails
 * and `isActivePerson` in `@otto-iq/shared` treats an *absent* status as
 * active — correct on the server, where the principal always comes from a real
 * row, and wrong here, where it turns an auth failure into a working session.
 *
 * The result was a sidebar you could browse with "Not signed in" in the footer.
 *
 * So: after sign-in there are exactly two places to be, and this decides which
 * — but *which build this is* decides it first, because the two consoles are
 * now separate Entra app registrations and a token minted for one is refused
 * on the other's routes.
 *
 * On the **client** build:
 *
 *   tenant attached          → the client console
 *   anything else            → a dead end with a way back out. Not a redirect
 *                              to `/master`: this build has no such route, so
 *                              that led to a 404.
 *
 * On the **master** build:
 *
 *   operator mode active     → the client shell, which is the whole point of
 *                              operator mode
 *   anything else            → `/master`, and `MasterShell`'s own guard decides
 *                              whether the app role is there
 *
 * The master case is the bug this second clause fixes. `session.tenant` used to
 * be checked first, so an Otto staff member who *also* has a `people` row —
 * anyone who added themselves to a demo tenant — signed in at the master origin
 * and got the client console rendered with a master-surface token. Every
 * tenant-scoped request then 403'd, which reads as a console full of "that did
 * not load" rather than as an auth boundary doing its job.
 *
 * Mounted around `<AppRoutes />` in `App.tsx`, and deliberately not inside the
 * `AppProviders` component body. It does render within the provider tree — it
 * calls `useSession`, so it must be under `ConsoleSessionProvider` — but it is
 * not part of the stack `AppProviders` renders, and that distinction is what
 * keeps the test suite working: `renderWithProviders` wraps that stack around
 * each page under test, so a gate inside it would send all 172 page tests to
 * the dead end. The app is gated; page tests keep testing pages, and this file
 * has its own.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Button, Card, EmptyState, IconShield } from '../components';
import { ApiError, getOperatorToken } from '../lib/api';
import { consoleSurface, signOut } from '../lib/auth';
import { useSession } from '../lib/session';
import styles from './auth-gate.module.css';

/** The API's code for "authenticated, but no workspace" — see `apps/api/src/auth/plugin.ts`. */
const NO_WORKSPACE = 'no_workspace';

/** The API's code for a workspace Otto has suspended. Same file. */
const WORKSPACE_SUSPENDED = 'workspace_suspended';

/** The account is known here, but signed in from a different Entra directory. */
const WRONG_DIRECTORY = 'wrong_directory';

function errorCodeOf(error: unknown): string | undefined {
  return error instanceof ApiError ? error.code : undefined;
}

/**
 * Copy for the dead end.
 *
 * A 401 that is *not* `no_workspace` means the token stopped being good — an
 * expired session, a revoked account. Telling that person their organisation
 * needs provisioning would send them to their Otto contact for a problem
 * signing in again would fix.
 */
function deadEndCopy(error: unknown, isMasterBuild = false): { title: string; body: string } {
  // Suspension is a decision, not a fault, and it is the one refusal where
  // "sign out and back in" is actively wrong — the next sign-in fails the same
  // way. Say who can undo it instead.
  if (errorCodeOf(error) === WORKSPACE_SUSPENDED) {
    return {
      title: 'This workspace is suspended',
      body: 'Otto IQ is paused for your organisation, so nothing runs and nobody can sign in. Your Otto contact can reactivate it.',
    };
  }

  // Not a fault either, and signing in again cannot fix it — the next token
  // carries the same directory. Name the two things that actually resolve it.
  if (errorCodeOf(error) === WRONG_DIRECTORY) {
    return {
      title: 'This account is in a different Microsoft directory',
      body: 'Your organisation only accepts sign-ins from its own Microsoft tenant, and this account belongs to another one. Sign in with an account in your organisation, or ask your Otto contact if you think the mapping is wrong.',
    };
  }

  if (errorCodeOf(error) === NO_WORKSPACE || error === undefined || error === null) {
    // The master build reaches this when Entra signed someone in who Otto does
    // not recognise at all. Telling them to ask about an organisation would be
    // the wrong advice — there is no organisation involved on this surface.
    if (isMasterBuild) {
      return {
        title: 'This account has no Otto IQ access',
        body: 'You are signed in with Microsoft, but the account is not recognised. Access here is granted in Entra, not in Otto IQ.',
      };
    }
    return {
      title: 'No organisation is attached to this sign-in',
      body: 'Your account is authenticated but not a member of an Otto IQ organisation yet. Your Otto contact can set this up for you.',
    };
  }
  return {
    title: 'Your session could not be verified',
    body: 'You are signed in with Microsoft, but Otto IQ could not confirm the session. Signing out and back in usually resolves it.',
  };
}

export function SessionGate({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  // Read at render rather than at module load: this is a static string in a
  // real build either way, and reading it here is what lets a test decide it.
  const isMasterBuild = consoleSurface === 'master';

  // Say nothing until `/me` has answered. Rendering the refusal first and the
  // console a moment later would flash "no organisation" at every legitimate
  // user on every page load.
  if (session.isLoading) {
    return (
      <div className={styles.wrap}>
        <p className={styles.body}>Signing you in…</p>
      </div>
    );
  }

  if (isMasterBuild) {
    // A 401 is worth stopping on even here — `MasterShell`'s guard would
    // otherwise tell someone with an expired token that they lack a role.
    if (session.error === undefined || session.error === null) {
      // Operator mode: the only legitimate reason for the client shell to
      // render in this build. `getOperatorToken()` is checked as well as
      // `session.operator`, and it is the one that matters — the token is set
      // synchronously just before `navigate('/')`, while `/me` is still
      // refetching in the background with `isLoading` already false. Trusting
      // the query alone bounced the operator straight back to `/master`.
      if (session.operator !== null || getOperatorToken() !== null) return <>{children}</>;

      return location.pathname.startsWith('/master') ? (
        <>{children}</>
      ) : (
        <Navigate to="/master" replace />
      );
    }
  } else if (session.tenant) {
    return <>{children}</>;
  }

  const { title, body } = deadEndCopy(session.error, isMasterBuild);

  return (
    <div className={styles.wrap}>
      <Card className={styles.card}>
        <EmptyState
          icon={<IconShield />}
          title={title}
          body={body}
          actions={
            <Button variant="primary" onClick={signOut}>
              Sign out
            </Button>
          }
        />
      </Card>
    </div>
  );
}

export default SessionGate;
